import { useEffect, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useLang } from "../context/LanguageContext";
import SearchSelect from "./SearchSelect";
import { SuccessPanel } from "./FormKit";
import { BRANDS, OTHER_MAKE, OTHER_MODEL } from "../data/vehicleData";
import { COUNTRIES, sortedCountries } from "../data/countries";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 5;
const MAX_SIZE = 5 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/png", "image/webp"];
const CURRENCIES = ["EUR", "AED", "GBP", "USD"];
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: currentYear + 1 - 1950 + 1 }, (_, i) => currentYear + 1 - i);

// Downscale large photos client-side so uploads stay small and reliable.
async function compressImage(file) {
  if (file.size <= 400 * 1024) return file;
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const w = bitmap.width, h = bitmap.height;
    const scale = Math.min(1, 1600 / Math.max(w, h));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.82));
    bitmap.close();
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    return file;
  }
}

export default function SellForm() {
  const { t, lang } = useLang();
  const s = t("sell");
  const [photos, setPhotos] = useState([]);
  const [photoErrs, setPhotoErrs] = useState([]);
  const [uploadStep, setUploadStep] = useState("");
  const [done, setDone] = useState(null);
  const [failedPhotos, setFailedPhotos] = useState([]);
  const [serverError, setServerError] = useState(false);
  const fileRef = useRef(null);

  const { register, handleSubmit, control, setValue, reset, formState: { errors, isSubmitting } } = useForm({ mode: "onSubmit" });
  const make = useWatch({ control, name: "make" });

  // Clear the model whenever the make changes — never show models of another manufacturer.
  useEffect(() => { setValue("model", null); setValue("model_other", ""); }, [make, setValue]);

  useEffect(() => () => photos.forEach((p) => URL.revokeObjectURL(p.url)), []); // eslint-disable-line

  const brandEntry = BRANDS.find((b) => b.make === make);
  const makeOptions = [
    ...BRANDS.map((b) => ({ value: b.make, label: b.make })),
    { value: OTHER_MAKE, label: s.optOtherMake },
  ];
  const modelOptions = make === OTHER_MAKE
    ? [{ value: OTHER_MODEL, label: s.optOtherModel }]
    : [
        ...(brandEntry ? brandEntry.models.map((m) => ({ value: m, label: m })) : []),
        { value: OTHER_MODEL, label: s.optOtherModel },
      ];

  const addFiles = async (fileList) => {
    const incoming = Array.from(fileList || []);
    const room = MAX_PHOTOS - photos.length;
    const errs = [];
    const next = [];
    for (const raw of incoming.slice(0, room)) {
      if (!ALLOWED.includes(raw.type)) { errs.push({ name: raw.name, msg: s.photoType }); continue; }
      if (raw.size > MAX_SIZE) { errs.push({ name: raw.name, msg: s.photoSize }); continue; }
      const processed = await compressImage(raw);
      next.push({ file: processed, url: URL.createObjectURL(processed), name: raw.name });
    }
    if (incoming.length > room) errs.push({ name: "", msg: s.photoTooMany });
    setPhotoErrs(errs);
    setPhotos((p) => [...p, ...next]);
  };

  const removePhoto = (idx) => {
    setPhotos((p) => {
      URL.revokeObjectURL(p[idx].url);
      return p.filter((_, i) => i !== idx);
    });
  };

  const onSubmit = async (data) => {
    setServerError(false);
    if (data.website) return; // honeypot — silently drop
    try {
      const res = await fetch(`${API}/vehicle-submissions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lang,
          make: data.make,
          model: data.make === OTHER_MAKE ? OTHER_MODEL : data.model,
          model_other: data.model_other || "",
          year: Number(data.year),
          mileage_km: Number(data.mileage_km),
          fuel_type: data.fuel_type,
          transmission: data.transmission,
          condition: data.condition,
          country: data.country,
          registration_spec: data.registration_spec || "",
          asking_price: Number(data.asking_price),
          currency: data.currency,
          full_name: data.full_name,
          email: data.email,
          phone: `${data.dial} ${String(data.phone_local).trim()}`.trim(),
          preferred_contact: data.preferred_contact,
          notes: data.notes || "",
          website: data.website || "",
        }),
      });
      if (!res.ok) throw new Error();
      const { id } = await res.json();
      const failed = [];
      for (let i = 0; i < photos.length; i++) {
        setUploadStep(`${i + 1}/${photos.length}`);
        const fd = new FormData();
        fd.append("file", photos[i].file);
        try {
          const r = await fetch(`${API}/vehicle-submissions/${id}/photos`, { method: "POST", body: fd });
          if (!r.ok) failed.push(photos[i].name);
        } catch {
          failed.push(photos[i].name);
        }
      }
      setFailedPhotos(failed);
      setUploadStep("");
      setDone(true);
    } catch {
      setUploadStep("");
      setServerError(true);
    }
  };

  const err = (key) => errors[key] && (
    <p className="mono text-[10px] text-[#D1C7BD] mt-1.5 tracking-[0.14em] uppercase" data-testid={`error-${key}`}>
      {errors[key].message || t("forms.required")}
    </p>
  );
  const label = (text, key, required = true) => (
    <label className="field-label" htmlFor={key}>{text} {required && <span className="text-[#D1C7BD]">*</span>}</label>
  );

  if (done) {
    return (
      <div>
        <SuccessPanel
          testid="sell-form-success"
          title={s.successTitle}
          body={s.successBody}
          again={s.again}
          onAgain={() => { setDone(false); setFailedPhotos([]); setPhotos([]); reset(); }}
        />
        {failedPhotos.length > 0 && (
          <p className="mono text-[11px] text-[#D1C7BD] mt-4 uppercase tracking-[0.14em]" data-testid="sell-photos-warning">
            {s.photosFailed}
          </p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate data-testid="sell-form" className="space-y-14">
      {/* 01 — VEHICLE DETAILS */}
      <fieldset>
        <legend className="eyebrow mb-6">01 — {s.secVehicle}</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
          <div data-testid="field-make">
            {label(s.f_make, "make")}
            <Controller name="make" control={control} rules={{ required: t("forms.required") }} render={({ field }) => (
              <SearchSelect testid="make" value={field.value} onChange={field.onChange}
                options={makeOptions} placeholder={s.phMake} emptyLabel={s.noMatches} />
            )} />
            {err("make")}
          </div>
          {make === OTHER_MAKE ? (
            <div data-testid="field-model_other">
              {label(s.f_modelOther, "model_other")}
              <input id="model_other" className="field-input" data-testid="input-model_other" placeholder={s.phModelOther}
                {...register("model_other", { required: t("forms.required"), maxLength: 80 })} />
              {err("model_other")}
            </div>
          ) : (
            <div data-testid="field-model">
              {label(s.f_model, "model")}
              <Controller name="model" control={control} rules={{ required: make ? t("forms.required") : false }} render={({ field }) => (
                <SearchSelect testid="model" value={field.value} onChange={field.onChange}
                  options={modelOptions} placeholder={make ? s.phModel : s.phMakeFirst}
                  disabled={!make} emptyLabel={s.noMatches} />
              )} />
              {err("model")}
            </div>
          )}
          <div data-testid="field-year">
            {label(s.f_year, "year")}
            <Controller name="year" control={control} rules={{ required: t("forms.required") }} render={({ field }) => (
              <SearchSelect testid="year" value={field.value} onChange={field.onChange}
                options={YEARS.map((y) => ({ value: y, label: String(y) }))} placeholder={s.phYear} emptyLabel={s.noMatches} />
            )} />
            {err("year")}
          </div>
          <div data-testid="field-mileage_km">
            {label(s.f_mileage, "mileage_km")}
            <input id="mileage_km" type="number" min="0" step="1" inputMode="numeric" className="field-input" data-testid="input-mileage_km"
              placeholder="0"
              {...register("mileage_km", {
                required: t("forms.required"),
                validate: (v) => (v !== "" && Number(v) >= 0 && Number(v) <= 2000000) || s.errMileage,
              })} />
            {err("mileage_km")}
          </div>
          <div data-testid="field-fuel_type">
            {label(s.f_fuel, "fuel_type")}
            <Controller name="fuel_type" control={control} rules={{ required: t("forms.required") }} render={({ field }) => (
              <SearchSelect testid="fuel" value={field.value} onChange={field.onChange}
                options={["petrol", "diesel", "hybrid", "plugin_hybrid", "electric", "other"].map((k) => ({ value: k, label: s.fuelOpts[k] }))}
                placeholder={s.phFuel} emptyLabel={s.noMatches} />
            )} />
            {err("fuel_type")}
          </div>
          <div data-testid="field-transmission">
            {label(s.f_transmission, "transmission")}
            <Controller name="transmission" control={control} rules={{ required: t("forms.required") }} render={({ field }) => (
              <SearchSelect testid="transmission" value={field.value} onChange={field.onChange}
                options={["automatic", "manual", "other"].map((k) => ({ value: k, label: s.transOpts[k] }))}
                placeholder={s.phTrans} emptyLabel={s.noMatches} />
            )} />
            {err("transmission")}
          </div>
          <div data-testid="field-condition">
            {label(s.f_condition, "condition")}
            <Controller name="condition" control={control} rules={{ required: t("forms.required") }} render={({ field }) => (
              <SearchSelect testid="condition" value={field.value} onChange={field.onChange}
                options={["excellent", "very_good", "good", "fair", "needs_repair"].map((k) => ({ value: k, label: s.condOpts[k] }))}
                placeholder={s.phCond} emptyLabel={s.noMatches} />
            )} />
            {err("condition")}
          </div>
          <div data-testid="field-country">
            {label(s.f_country, "country")}
            <Controller name="country" control={control} rules={{ required: t("forms.required") }} render={({ field }) => (
              <SearchSelect testid="country" value={field.value} onChange={field.onChange}
                options={sortedCountries(lang).map((c) => ({ value: c.code, label: c.name }))}
                placeholder={s.phCountry} emptyLabel={s.noMatches} />
            )} />
            {err("country")}
          </div>
          <div data-testid="field-registration_spec">
            {label(s.f_registration, "registration_spec", false)}
            <input id="registration_spec" className="field-input" data-testid="input-registration_spec"
              placeholder={s.phRegistration} maxLength={120}
              {...register("registration_spec", { maxLength: 120 })} />
          </div>
        </div>
      </fieldset>

      {/* 02 — ASKING PRICE */}
      <fieldset>
        <legend className="eyebrow mb-6">02 — {s.secPrice}</legend>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6">
          <div className="md:col-span-2" data-testid="field-asking_price">
            {label(s.f_price, "asking_price")}
            <input id="asking_price" type="number" min="0" step="1" inputMode="numeric" className="field-input" data-testid="input-asking_price"
              {...register("asking_price", {
                required: t("forms.required"),
                validate: (v) => (v !== "" && Number(v) > 0 && Number(v) <= 100000000) || s.errPrice,
              })} />
            {err("asking_price")}
            <p className="mono text-[10px] text-[#666663] mt-2 tracking-[0.14em] uppercase" data-testid="price-note">{s.priceNote}</p>
          </div>
          <div data-testid="field-currency">
            {label(s.f_currency, "currency")}
            <Controller name="currency" control={control} rules={{ required: t("forms.required") }} render={({ field }) => (
              <SearchSelect testid="currency" value={field.value} onChange={field.onChange}
                options={CURRENCIES.map((c) => ({ value: c, label: c }))} placeholder={s.phCurrency} emptyLabel={s.noMatches} />
            )} />
            {err("currency")}
          </div>
        </div>
      </fieldset>

      {/* 03 — PHOTOS */}
      <fieldset>
        <legend className="eyebrow mb-6">03 — {s.secPhotos}</legend>
        <p className="mono text-[10px] text-[#666663] mb-4 tracking-[0.14em] uppercase" data-testid="photos-hint">{s.photosHint}</p>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="photo-input" />
        <button type="button" className="btn justify-between" onClick={() => fileRef.current && fileRef.current.click()}
          data-testid="photo-add-btn" disabled={photos.length >= MAX_PHOTOS}>
          <span>{s.photosAdd}</span><span aria-hidden="true">→</span>
        </button>
        {photoErrs.map((p, i) => (
          <p key={i} className="mono text-[10px] text-[#D1C7BD] mt-2 tracking-[0.14em] uppercase" data-testid={`photo-error-${i}`}>
            {p.name ? `${p.name}: ` : ""}{p.msg}
          </p>
        ))}
        {photos.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6" data-testid="photo-previews">
            {photos.map((p, i) => (
              <div key={p.url} className="relative border border-white/10 group" data-testid={`photo-item-${i}`}>
                <img src={p.url} alt={`${s.f_photos} ${i + 1}`} className="w-full h-24 object-cover" />
                <button type="button" onClick={() => removePhoto(i)} data-testid={`photo-remove-${i}`}
                  aria-label={`${s.photoRemove} ${i + 1}`}
                  className="absolute top-1 right-1 w-6 h-6 bg-[#050607]/85 border border-white/20 text-[#F3F3F0] text-[11px] leading-none opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity duration-200 cursor-pointer">
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </fieldset>

      {/* 04 — CONTACT DETAILS */}
      <fieldset>
        <legend className="eyebrow mb-6">04 — {s.secContact}</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
          <div data-testid="field-full_name">
            {label(s.f_name, "full_name")}
            <input id="full_name" className="field-input" data-testid="input-full_name"
              {...register("full_name", { required: t("forms.required"), maxLength: 120 })} />
            {err("full_name")}
          </div>
          <div data-testid="field-email">
            {label(s.f_email, "email")}
            <input id="email" type="email" className="field-input" data-testid="input-email"
              {...register("email", {
                required: t("forms.required"),
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t("forms.invalidEmail") },
              })} />
            {err("email")}
          </div>
          <div className="md:col-span-2" data-testid="field-phone">
            {label(s.f_phone, "phone")}
            <div className="flex gap-3">
              <div className="w-[120px] shrink-0">
                <Controller name="dial" control={control} rules={{ required: t("forms.required") }} render={({ field }) => (
                  <SearchSelect testid="dial" value={field.value} onChange={field.onChange}
                    options={COUNTRIES.map((c) => ({ value: c.dial, label: c.dial, code: c.code }))}
                    placeholder="+…" emptyLabel={s.noMatches} />
                )} />
              </div>
              <input id="phone" type="tel" className="field-input flex-1" data-testid="input-phone" placeholder="+34 622 181 686"
                {...register("phone_local", {
                  required: t("forms.required"),
                  validate: (v) => {
                    const digits = String(v || "").replace(/\D/g, "");
                    return (digits.length >= 6 && digits.length <= 15) || s.errPhone;
                  },
                })} />
            </div>
            {err("phone_local")}
          </div>
          <div data-testid="field-preferred_contact">
            {label(s.f_contactMethod, "preferred_contact")}
            <Controller name="preferred_contact" control={control} rules={{ required: t("forms.required") }} render={({ field }) => (
              <SearchSelect testid="contact_method" value={field.value} onChange={field.onChange}
                options={["email", "phone", "whatsapp"].map((k) => ({ value: k, label: s.contactOpts[k] }))}
                placeholder={s.phContact} emptyLabel={s.noMatches} />
            )} />
            {err("preferred_contact")}
          </div>
          <div className="md:col-span-2" data-testid="field-notes">
            {label(s.f_notes, "notes", false)}
            <textarea id="notes" rows={4} className="field-input" data-testid="input-notes" placeholder={s.phNotes} maxLength={2000}
              {...register("notes", { maxLength: 2000 })} />
          </div>
        </div>
      </fieldset>

      {/* honeypot — invisible to humans */}
      <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden"
        style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}
        {...register("website")} data-testid="input-website-honeypot" />

      {/* CONSENT + SUBMIT */}
      <div className="border-t border-white/10 pt-8 flex flex-col gap-5">
        <div data-testid="field-consent">
          <label className="flex items-start gap-3 cursor-pointer" htmlFor="consent">
            <input id="consent" type="checkbox" className="w-[14px] h-[14px] mt-[3px] accent-[#D1C7BD] cursor-pointer shrink-0"
              data-testid="input-consent"
              {...register("consent", { required: t("forms.required") })} />
            <span className="text-[12px] text-[#999995] leading-relaxed">
              {s.consentBefore}{" "}
              <a href="/privacy-policy" className="text-[#D1C7BD] underline underline-offset-4 hover:text-[#F3F3F0] transition-colors" data-testid="consent-privacy-link">
                {s.privacyLink}
              </a>
              {s.consentAfter}
            </span>
          </label>
          {err("consent")}
        </div>
        {serverError && <p className="mono text-[11px] text-[#D1C7BD] uppercase tracking-[0.14em]" data-testid="sell-form-error">{t("forms.error")}</p>}
        {uploadStep && <p className="mono text-[11px] text-[#999995] uppercase tracking-[0.14em]" data-testid="photos-progress">{s.uploading} — {uploadStep}</p>}
        <button type="submit" disabled={isSubmitting} className="btn btn-solid justify-between md:self-start" data-testid="sell-form-submit-btn">
          <span>{isSubmitting ? (uploadStep ? `${s.uploading} — ${uploadStep}` : t("forms.sending")) : s.submit}</span>
          <span aria-hidden="true">→</span>
        </button>
      </div>
    </form>
  );
}
